import { ChatAppResponse, getCitationFilePath } from "../../../api";

type HtmlParsedAnswer = {
  answerHtml: string;
  citations: string[];
  followupQuestions: string[];
};

// CHANGED: plain HTML escape, replaces the SSR React render below.
const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export function parseAnswerToHtml(
  answer: ChatAppResponse,
  isStreaming: boolean,
  onCitationClicked: (citationFilePath: string) => void,
  selectedUseCase: string,
): HtmlParsedAnswer {
  const citations: string[] = [];
  const followupQuestions: string[] = [];

  // ✅ Guard against incomplete or streaming responses
  const message = answer?.choices?.[0]?.message;
  const context = answer?.choices?.[0]?.context;
  const messageContent = message?.content ?? "";
  const contextDataPoints = context?.data_points ?? [];
  const hasDataPoints = contextDataPoints.length > 0;

  // Extract any follow-up questions that might be in the answer
  let parsedAnswer = messageContent.replace(
    /<<([^>>]+)>>/g,
    (match, content) => {
      // Preserve visualization markers — handled by injectVisualizations later
      if (content.startsWith("viz:")) {
        return match;
      }
      followupQuestions.push(content);
      return "";
    },
  );

  parsedAnswer = parsedAnswer.trim();

  // Omit a citation that is still being typed during streaming
  if (isStreaming) {
    let lastIndex = parsedAnswer.length;
    for (let i = parsedAnswer.length - 1; i >= 0; i--) {
      if (parsedAnswer[i] === "]") break;
      else if (parsedAnswer[i] === "[") {
        lastIndex = i;
        break;
      }
    }
    parsedAnswer = parsedAnswer.substring(0, lastIndex);
  }

  // Handle cases without data points
  if (!hasDataPoints) {
    return {
      answerHtml: parsedAnswer,
      citations,
      followupQuestions,
    };
  }

  // Handle citations normally if we do have data points
  const parts = parsedAnswer.split(/\[([^\]]+)\]/g);
  const fragments: string[] = parts.map((part, index) => {
    const tmp_split = part.split(
      /pdf|docx|pptx|xlsx|doc|xls|msg|txt|png|jpg|jpeg/,
    );
    if (index % 2 === 0 || tmp_split.length < 2) {
      return part;
    } else {
      let citationIndex: number;
      if (citations.indexOf(part) !== -1) {
        citationIndex = citations.indexOf(part) + 1;
      } else {
        citations.push(part);
        citationIndex = citations.length;
      }

      // CHANGED: was `renderToStaticMarkup(<a ...>...)` which spins up a full
      // React SSR render per citation, per streaming tick (~2ms × N citations
      // × 30Hz). Replaced with a plain string template — ~100x cheaper.
      // The onClick handler is dropped because it never worked anyway in the
      // SSR output (handlers don't survive renderToStaticMarkup); citation
      // clicks are handled by the citation list in Answer.tsx.
      // `getCitationFilePath` and `onCitationClicked` are no longer called
      // here — keeping the signature stable so callers don't need to change.
      void getCitationFilePath; // silence unused-import lint if applicable
      void onCitationClicked;
      void selectedUseCase;

      return `<a class="supContainer" title="${escapeHtml(part)}"><sup>${citationIndex}</sup></a>`;
    }
  });

  return {
    answerHtml: fragments.join(""),
    citations,
    followupQuestions,
  };
}